import { useState, useEffect, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  Search,
  ChevronDown,
  ChevronUp,
  MapPin,
  DollarSign,
  X,
  Globe,
  Video,
} from 'lucide-react'
import { talentService } from '../../services/talentService'
import { MultiSelect } from '../../components/ui/MultiSelect'
import type {
  TalentProfileListItem,
  Skill,
  ProfileOption,
  Language,
  ExpertiseOffering,
  Country,
} from '../../types/talent'

interface Filters {
  expertiseOfferings: string[]
  mainFields: string[]
  languages: string[]
  countries: string[]
  minRate: string
  maxRate: string
  preferredFormats: string[]
  skills: string[]
}

const emptyFilters: Filters = {
  expertiseOfferings: [],
  mainFields: [],
  languages: [],
  countries: [],
  minRate: '',
  maxRate: '',
  preferredFormats: [],
  skills: [],
}

export default function TalentSearchPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [allTalents, setAllTalents] = useState<TalentProfileListItem[]>([])
  const [error, setError] = useState<string | null>(null)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [filters, setFilters] = useState<Filters>(emptyFilters)

  // Filter option data
  const [expertiseOptions, setExpertiseOptions] = useState<ExpertiseOffering[]>([])
  const [mainFieldOptions, setMainFieldOptions] = useState<ProfileOption[]>([])
  const [languageOptions, setLanguageOptions] = useState<Language[]>([])
  const [countryOptions, setCountryOptions] = useState<Country[]>([])
  const [formatOptions, setFormatOptions] = useState<ProfileOption[]>([])
  const [skills, setSkills] = useState<Skill[]>([])

  // Load data on mount
  useEffect(() => {
    loadTalents()
    loadFilterOptions()
  }, [])

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const loadTalents = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await talentService.getTalents()
      setAllTalents(response.results)
    } catch {
      setError('Failed to load experts. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const loadFilterOptions = async () => {
    try {
      const [mainFields, expertise, languages, formats, countriesData, skillsData] =
        await Promise.all([
          talentService.getProfileOptions('main_field'),
          talentService.getExpertiseOfferings(),
          talentService.getLanguages(),
          talentService.getProfileOptions('meeting_format'),
          talentService.getCountries(),
          talentService.getSkills(),
        ])
      setMainFieldOptions(mainFields)
      setExpertiseOptions(expertise)
      setLanguageOptions(languages)
      setFormatOptions(formats)
      setCountryOptions(countriesData)
      setSkills(skillsData)
    } catch (err) {
      console.error('Error loading filter options:', err)
    }
  }

  // Build lookup maps for filter matching
  const expertiseNameMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const o of expertiseOptions) {
      map.set(o.id, o.name)
      if (o.sub_options) {
        for (const sub of o.sub_options) {
          map.set(sub.id, sub.name)
        }
      }
    }
    return map
  }, [expertiseOptions])

  const mainFieldNameMap = useMemo(
    () => new Map(mainFieldOptions.map((o) => [o.id, o.name])),
    [mainFieldOptions]
  )

  const languageNameMap = useMemo(
    () => new Map(languageOptions.map((l) => [l.id, l.name])),
    [languageOptions]
  )

  const countryNameMap = useMemo(
    () => new Map(countryOptions.map((c) => [c.id, c.name])),
    [countryOptions]
  )

  const formatNameMap = useMemo(
    () => new Map(formatOptions.map((f) => [f.id, f.name])),
    [formatOptions]
  )

  // Apply filters
  const applyFilters = useCallback(
    (talentList: TalentProfileListItem[]) => {
      let filtered = talentList

      // Free text search across everything
      if (debouncedQuery.trim()) {
        const q = debouncedQuery.toLowerCase()
        filtered = filtered.filter(
          (t) =>
            t.full_name?.toLowerCase().includes(q) ||
            t.headline?.toLowerCase().includes(q) ||
            t.skills.some((s) => s.toLowerCase().includes(q)) ||
            t.expertise_offerings.some((e) => e.toLowerCase().includes(q)) ||
            t.main_fields?.some((f) => f.toLowerCase().includes(q)) ||
            t.languages.some((l) => l.toLowerCase().includes(q)) ||
            t.country?.toLowerCase().includes(q) ||
            t.preferred_formats.some((f) => f.toLowerCase().includes(q)) ||
            t.display_id.toLowerCase().includes(q)
        )
      }

      // Service type filter
      if (filters.expertiseOfferings.length > 0) {
        const names = filters.expertiseOfferings
          .map((id) => expertiseNameMap.get(id))
          .filter(Boolean)
        filtered = filtered.filter((t) =>
          names.some((name) => t.expertise_offerings.some((e) => e === name))
        )
      }

      // Field filter
      if (filters.mainFields.length > 0) {
        const names = filters.mainFields
          .map((id) => mainFieldNameMap.get(id))
          .filter(Boolean)
        filtered = filtered.filter((t) =>
          names.some((name) => t.main_fields?.some((f) => f === name))
        )
      }

      // Language filter
      if (filters.languages.length > 0) {
        const names = filters.languages
          .map((id) => languageNameMap.get(id))
          .filter(Boolean)
        filtered = filtered.filter((t) =>
          names.some((name) => t.languages.some((l) => l === name))
        )
      }

      // Country filter
      if (filters.countries.length > 0) {
        const names = filters.countries
          .map((id) => countryNameMap.get(id))
          .filter(Boolean)
        filtered = filtered.filter((t) => names.some((name) => t.country === name))
      }

      // Rate range
      if (filters.minRate || filters.maxRate) {
        filtered = filtered.filter((t) => {
          if (t.hourly_rate_min === null && t.hourly_rate_max === null) return false
          const min = t.hourly_rate_min ?? 0
          const max = t.hourly_rate_max ?? Infinity
          if (filters.minRate && max < parseInt(filters.minRate)) return false
          if (filters.maxRate && min > parseInt(filters.maxRate)) return false
          return true
        })
      }

      // Format filter
      if (filters.preferredFormats.length > 0) {
        const names = filters.preferredFormats
          .map((id) => formatNameMap.get(id))
          .filter(Boolean)
        filtered = filtered.filter((t) =>
          names.some((name) => t.preferred_formats.some((f) => f === name))
        )
      }

      // Skills filter
      if (filters.skills.length > 0) {
        filtered = filtered.filter((t) =>
          filters.skills.some((skillName) =>
            t.skills.some((s) => s.toLowerCase() === skillName.toLowerCase())
          )
        )
      }

      return filtered
    },
    [
      debouncedQuery,
      filters,
      expertiseNameMap,
      mainFieldNameMap,
      languageNameMap,
      countryNameMap,
      formatNameMap,
    ]
  )

  const talents = useMemo(() => applyFilters(allTalents), [allTalents, applyFilters])

  const updateFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }

  const clearFilters = () => {
    setFilters(emptyFilters)
    setSearchQuery('')
    setDebouncedQuery('')
  }

  // Build active filter chips
  const activeChips = useMemo(() => {
    const chips: { key: string; label: string; onRemove: () => void }[] = []

    for (const id of filters.expertiseOfferings) {
      const name = expertiseNameMap.get(id)
      if (name)
        chips.push({
          key: `eo-${id}`,
          label: name,
          onRemove: () =>
            updateFilter(
              'expertiseOfferings',
              filters.expertiseOfferings.filter((x) => x !== id)
            ),
        })
    }
    for (const id of filters.mainFields) {
      const name = mainFieldNameMap.get(id)
      if (name)
        chips.push({
          key: `mf-${id}`,
          label: name,
          onRemove: () =>
            updateFilter(
              'mainFields',
              filters.mainFields.filter((x) => x !== id)
            ),
        })
    }
    for (const id of filters.languages) {
      const name = languageNameMap.get(id)
      if (name)
        chips.push({
          key: `lang-${id}`,
          label: name,
          onRemove: () =>
            updateFilter(
              'languages',
              filters.languages.filter((x) => x !== id)
            ),
        })
    }
    for (const id of filters.countries) {
      const name = countryNameMap.get(id)
      if (name)
        chips.push({
          key: `co-${id}`,
          label: name,
          onRemove: () =>
            updateFilter(
              'countries',
              filters.countries.filter((x) => x !== id)
            ),
        })
    }
    for (const id of filters.preferredFormats) {
      const name = formatNameMap.get(id)
      if (name)
        chips.push({
          key: `fmt-${id}`,
          label: name,
          onRemove: () =>
            updateFilter(
              'preferredFormats',
              filters.preferredFormats.filter((x) => x !== id)
            ),
        })
    }
    for (const skillName of filters.skills) {
      chips.push({
        key: `sk-${skillName}`,
        label: skillName,
        onRemove: () =>
          updateFilter(
            'skills',
            filters.skills.filter((x) => x !== skillName)
          ),
      })
    }
    if (filters.minRate)
      chips.push({
        key: 'minRate',
        label: `Min $${filters.minRate}/hr`,
        onRemove: () => updateFilter('minRate', ''),
      })
    if (filters.maxRate)
      chips.push({
        key: 'maxRate',
        label: `Max $${filters.maxRate}/hr`,
        onRemove: () => updateFilter('maxRate', ''),
      })

    return chips
  }, [
    filters,
    expertiseNameMap,
    mainFieldNameMap,
    languageNameMap,
    countryNameMap,
    formatNameMap,
  ])

  // Dropdown option builders
  const expertiseSelectOptions = useMemo(() => {
    const opts: { id: string; label: string }[] = []
    for (const o of expertiseOptions) {
      opts.push({ id: o.id, label: o.name })
      if (o.sub_options) {
        for (const sub of o.sub_options) {
          opts.push({ id: sub.id, label: `  ${sub.name}` })
        }
      }
    }
    return opts
  }, [expertiseOptions])

  const mainFieldSelectOptions = useMemo(
    () => mainFieldOptions.map((o) => ({ id: o.id, label: o.name })),
    [mainFieldOptions]
  )

  const languageSelectOptions = useMemo(
    () => languageOptions.map((l) => ({ id: l.id, label: l.name })),
    [languageOptions]
  )

  const countrySelectOptions = useMemo(
    () => countryOptions.map((c) => ({ id: c.id, label: c.name })),
    [countryOptions]
  )

  const formatSelectOptions = useMemo(
    () => formatOptions.map((f) => ({ id: f.id, label: f.name })),
    [formatOptions]
  )

  const skillSelectOptions = useMemo(
    () => skills.map((s) => ({ id: s.name, label: s.name })),
    [skills]
  )

  const hasActiveFilters = activeChips.length > 0 || debouncedQuery.trim().length > 0

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-secondary-900">Find Experts</h1>
        <p className="text-secondary-500 mt-2">
          Search by name, field, skill, service type, or keyword
        </p>
      </div>

      {/* Search & Filters Container */}
      <div className="bg-white rounded-2xl border border-secondary-200 shadow-sm mb-8">
        {/* Search Bar */}
        <div className="p-4 pb-0">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-secondary-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, expertise, field, language, location..."
              className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-secondary-200 bg-secondary-50 focus:bg-white focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none text-base transition-colors"
            />
          </div>
        </div>

        {/* Primary Filters Row */}
        <div className="p-4 flex flex-wrap items-start gap-3">
          <div className="flex-1 min-w-[200px] max-w-[280px]">
            <MultiSelect
              options={expertiseSelectOptions}
              value={filters.expertiseOfferings}
              onChange={(v) => updateFilter('expertiseOfferings', v)}
              placeholder="Service type..."
              searchable
            />
          </div>

          <div className="flex-1 min-w-[200px] max-w-[280px]">
            <MultiSelect
              options={mainFieldSelectOptions}
              value={filters.mainFields}
              onChange={(v) => updateFilter('mainFields', v)}
              placeholder="Field..."
              searchable
            />
          </div>

          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-secondary-200 bg-white text-sm font-medium text-secondary-600 hover:bg-secondary-50 hover:border-secondary-300 transition-colors"
          >
            Advanced Filters
            {showAdvanced ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1 px-3 py-2.5 text-sm text-secondary-500 hover:text-secondary-700 transition-colors"
            >
              <X className="h-4 w-4" />
              Clear all
            </button>
          )}
        </div>

        {/* Advanced Filters (collapsible) */}
        {showAdvanced && (
          <div className="px-4 pb-4 border-t border-secondary-100 pt-4">
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-xs font-medium text-secondary-500 mb-1.5">
                  Language
                </label>
                <MultiSelect
                  options={languageSelectOptions}
                  value={filters.languages}
                  onChange={(v) => updateFilter('languages', v)}
                  placeholder="Any language..."
                  searchable
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-secondary-500 mb-1.5">
                  Location
                </label>
                <MultiSelect
                  options={countrySelectOptions}
                  value={filters.countries}
                  onChange={(v) => updateFilter('countries', v)}
                  placeholder="Any location..."
                  searchable
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-secondary-500 mb-1.5">
                  Budget range ($/hr)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={filters.minRate}
                    onChange={(e) => updateFilter('minRate', e.target.value)}
                    className="input text-sm py-2"
                  />
                  <span className="text-secondary-400 text-sm">-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={filters.maxRate}
                    onChange={(e) => updateFilter('maxRate', e.target.value)}
                    className="input text-sm py-2"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-secondary-500 mb-1.5">
                  Meeting format
                </label>
                <MultiSelect
                  options={formatSelectOptions}
                  value={filters.preferredFormats}
                  onChange={(v) => updateFilter('preferredFormats', v)}
                  placeholder="Any format..."
                  searchable={false}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-secondary-500 mb-1.5">
                  Skills
                </label>
                <MultiSelect
                  options={skillSelectOptions}
                  value={filters.skills}
                  onChange={(v) => updateFilter('skills', v)}
                  placeholder="Any skill..."
                  searchable
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Active Filter Chips */}
      {activeChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <span className="text-xs font-medium text-secondary-400 uppercase tracking-wide">
            Active filters:
          </span>
          {activeChips.map((chip) => (
            <span
              key={chip.key}
              className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 bg-primary-50 text-primary-700 rounded-full text-sm border border-primary-100"
            >
              {chip.label}
              <button
                onClick={chip.onRemove}
                className="p-0.5 rounded-full hover:bg-primary-100 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
          <button onClick={loadTalents} className="ml-2 underline">
            Retry
          </button>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="card p-6 animate-pulse">
              <div className="flex items-start gap-4">
                <div className="h-14 w-14 rounded-full bg-secondary-200" />
                <div className="flex-1">
                  <div className="h-5 w-28 bg-secondary-200 rounded mb-2" />
                  <div className="h-4 w-40 bg-secondary-100 rounded" />
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <div className="h-6 w-16 bg-secondary-100 rounded-full" />
                <div className="h-6 w-20 bg-secondary-100 rounded-full" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No Results */}
      {!isLoading && talents.length === 0 && (
        <div className="text-center py-16">
          <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Search className="h-8 w-8 text-secondary-400" />
          </div>
          <h3 className="text-lg font-medium text-secondary-900 mb-2">No experts found</h3>
          <p className="text-secondary-500 mb-4">Try adjusting your filters or search terms</p>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="btn btn-secondary">
              Clear all filters
            </button>
          )}
        </div>
      )}

      {/* Results Grid */}
      {!isLoading && talents.length > 0 && (
        <>
          {/* Results count */}
          <p className="text-sm text-secondary-500 mb-4">
            {talents.length} expert{talents.length !== 1 ? 's' : ''} found
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="talent-grid">
            {talents.map((talent) => (
              <Link
                key={talent.id}
                to={`/talents/${talent.id}`}
                className="card p-6 hover:shadow-soft transition-all duration-200 hover:border-primary-200 group"
                data-testid="talent-card"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-4">
                    <div className="h-14 w-14 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-lg shadow-primary-500/20 overflow-hidden">
                      {talent.profile_picture_url ? (
                        <img
                          src={talent.profile_picture_url}
                          alt={talent.full_name || talent.display_id}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="text-white font-bold text-lg">
                          {talent.full_name
                            ? talent.full_name
                                .split(' ')
                                .map((n) => n[0])
                                .join('')
                                .slice(0, 2)
                                .toUpperCase()
                            : talent.display_id.split('-')[1]?.slice(0, 2) || 'T'}
                        </span>
                      )}
                    </div>
                    <div>
                      <h3
                        className="font-semibold text-secondary-900 group-hover:text-primary-600 transition-colors"
                        data-testid="talent-display-id"
                      >
                        {talent.full_name || talent.display_id}
                      </h3>
                      {talent.main_fields && talent.main_fields.length > 0 && (
                        <p className="text-sm text-primary-600 font-medium">
                          {talent.main_fields.join(', ')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Headline */}
                <p className="text-sm text-secondary-500 line-clamp-1 mb-3">{talent.headline}</p>

                {/* Expertise Offerings */}
                {talent.expertise_offerings.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {talent.expertise_offerings.map((offering) => (
                      <span
                        key={offering}
                        className="px-2.5 py-1 bg-accent-50 text-accent-700 text-xs rounded-lg font-medium"
                      >
                        {offering}
                      </span>
                    ))}
                  </div>
                )}

                {/* Skills */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {talent.skills.slice(0, 3).map((skill) => (
                    <span
                      key={skill}
                      className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-lg font-medium"
                    >
                      {skill}
                    </span>
                  ))}
                  {talent.skills.length > 3 && (
                    <span className="px-2.5 py-1 bg-secondary-50 text-secondary-400 text-xs rounded-lg">
                      +{talent.skills.length - 3}
                    </span>
                  )}
                </div>

                {/* Languages & Format */}
                {(talent.languages.length > 0 || talent.preferred_formats.length > 0) && (
                  <div className="flex items-center gap-3 text-xs text-secondary-500 mb-3">
                    {talent.languages.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Globe className="h-3.5 w-3.5" />
                        {talent.languages.slice(0, 2).join(', ')}
                        {talent.languages.length > 2 && ` +${talent.languages.length - 2}`}
                      </span>
                    )}
                    {talent.preferred_formats.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Video className="h-3.5 w-3.5" />
                        {talent.preferred_formats.join(', ')}
                      </span>
                    )}
                  </div>
                )}

                {/* Meta Info */}
                <div className="flex items-center gap-4 text-sm text-secondary-500 pt-3 border-t border-secondary-100">
                  {talent.hourly_rate_display && (
                    <span className="flex items-center gap-1.5 font-medium text-secondary-700">
                      <DollarSign className="h-4 w-4 text-accent-500" />
                      {talent.hourly_rate_display}
                    </span>
                  )}
                  {talent.country && (
                    <span className="flex items-center gap-1.5">
                      <MapPin className="h-4 w-4" />
                      {talent.country}
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>

          {/* Bottom count */}
          <p className="mt-8 text-sm text-secondary-500 text-center">
            Showing {talents.length} expert{talents.length !== 1 ? 's' : ''}
          </p>
        </>
      )}
    </div>
  )
}
